<div class="ws-step">
  <div class="ws-step-header text-center">
    <div style="font-size: 64px; color: #4caf50;" class="mb-3">
      <i class="bi bi-check-circle-fill"></i>
    </div>
    <h3>Your workspace is configured</h3>
    <p class="text-muted">Now put your team to work and let Salesmax build the real activity view you came here to evaluate.</p>
  </div>

  <div class="ws-step-body">
    <section class="ws-finish-activation" aria-labelledby="activation-plan-title">
      <span class="ws-finish-activation__eyebrow">Your trial reward is live</span>
      <h4 id="activation-plan-title">Earn one admin seat free for your first year</h4>
      <p class="ws-finish-activation__intro">This is deliberately simple: add 3 reps, get Salesmax onto their phones, and let 500 real calls flow in before the trial ends.</p>

      <ol class="ws-finish-activation__steps">
        <li [class.is-complete]="!teamSetupIncomplete">
          <span class="ws-finish-activation__number" aria-hidden="true">
            @if (teamSetupIncomplete) { 1 } @else { <i class="bi bi-check-lg"></i> }
          </span>
          <div class="ws-finish-activation__step-copy">
            <h5>Add 3 sales reps</h5>
            <p>{{ invitedRepresentativeCount }} of {{ requiredRepresentatives }} invited. The workspace admin is not counted as a rep.</p>
          </div>
          @if (teamSetupIncomplete) {
            <button type="button" class="btn btn-primary" (click)="reviewTeamSetup()">Add remaining reps</button>
          } @else {
            <span class="ws-finish-activation__done"><i class="bi bi-check-circle-fill me-1"></i>3 reps invited</span>
          }
        </li>
        <li>
          <span class="ws-finish-activation__number" aria-hidden="true">2</span>
          <div class="ws-finish-activation__step-copy">
            <h5>Install Salesmax on all 3 phones</h5>
            <p>Open the app once on each phone. The trial card will verify every rep and show exactly what still needs attention.</p>
          </div>
          <div class="ws-finish-activation__actions">
            <a [href]="appStoreUrl" target="_blank" rel="noopener" class="btn btn-primary btn-sm">
              <i class="bi bi-google-play me-1"></i> Open Google Play
            </a>
            <button type="button" class="btn btn-outline-primary btn-sm" (click)="copyAppLink()">
              <i class="bi" [ngClass]="appLinkCopied ? 'bi-check-lg' : 'bi-link-45deg'"></i>
              {{ appLinkCopied ? 'Link copied' : 'Copy link for reps' }}
            </button>
          </div>
        </li>
        <li>
          <span class="ws-finish-activation__number" aria-hidden="true">3</span>
          <div class="ws-finish-activation__step-copy">
            <h5>Log {{ requiredTrackedCalls }} tracked calls</h5>
            <p>Calls made by those reps after this trial started count automatically. Your own admin calls do not.</p>
          </div>
          <span class="ws-finish-activation__pending">Tracked live</span>
        </li>
      </ol>

      <div class="ws-finish-activation__outcome">
        <i class="bi bi-trophy-fill" aria-hidden="true"></i>
        <strong>Every real call does two jobs.</strong>
        <span>It builds the team visibility you are evaluating and moves you toward your free seat.</span>
      </div>
      <p class="ws-finish-activation__eligibility">For self-created trials. Support confirms eligibility and applies the seat when you subscribe.</p>
    </section>

    <!-- Setup summary -->
    <h6 class="mb-3">Setup Summary</h6>
    @for (step of steps; track step.key; let i = $index) {
      @if (step.key !== 'finish') {
        <div class="d-flex align-items-center py-2 border-bottom">
          <div class="me-3">
            @switch (getStepStatus(i)) {
              @case ('completed') {
                <i class="bi bi-check-circle-fill mr-2 text-success" style="font-size: 18px;"></i>
              }
              @case ('skipped') {
                @if (step.key === 'team') {
                  <i class="bi bi-exclamation-circle-fill mr-2 text-warning" style="font-size: 18px;"></i>
                } @else {
                  <i class="bi bi-dash-circle mr-2 text-muted" style="font-size: 18px;"></i>
                }
              }
              @default {
                <i class="bi bi-circle mr-2 text-muted" style="font-size: 18px;"></i>
              }
            }
          </div>
          <div class="flex-fill">
            <span [ngClass]="{
              'text-muted text-decoration-line-through': getStepStatus(i) === 'skipped' && step.key !== 'team',
              'ws-finish-team-label': step.key === 'team' && teamSetupIncomplete
            }">
              {{ step.label }}
            </span>
          </div>
          <div>
            @switch (getStepStatus(i)) {
              @case ('completed') { <span class="badge bg-success-subtle text-success">Done</span> }
              @case ('skipped') {
                @if (step.key === 'team') {
                  <span class="badge bg-warning-subtle text-warning">Recommended</span>
                } @else {
                  <span class="badge bg-secondary-subtle text-secondary">Skipped</span>
                }
              }
            }
          </div>
        </div>
      }
    }

    <!-- Training Slideshow -->
    <div class="mt-4 p-3 bg-light rounded">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h6 class="mb-0"><i class="bi bi-play-circle me-2"></i>Quick Training Guide</h6>
        <button class="btn btn-sm" [class]="showTraining ? 'btn-outline-secondary' : 'btn-primary'" (click)="showTraining = !showTraining">
          {{ showTraining ? 'Hide' : 'Watch Now' }}
        </button>
      </div>
      <p class="text-muted mb-0">Learn how to set up the app and get your team started in minutes.</p>
      @if (showTraining) {
        <!-- Toolbar -->
        <div class="mt-3 d-flex align-items-center justify-content-end gap-2">
          <button class="btn btn-outline-secondary btn-sm" (click)="copyLink()" title="Copy link">
            <i class="bi" [ngClass]="linkCopied ? 'bi-check-lg text-success' : 'bi-link-45deg'"></i>
            {{ linkCopied ? 'Copied!' : 'Copy Link' }}
          </button>
          <a [href]="trainingDownloadUrl" target="_blank" class="btn btn-outline-secondary btn-sm" title="Download as PowerPoint">
            <i class="bi bi-download me-1"></i> Download
          </a>
          <button class="btn btn-outline-secondary btn-sm" (click)="toggleFullscreen()" title="Fullscreen">
            <i class="bi" [ngClass]="isFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'"></i>
            {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
          </button>
        </div>
        <!-- Slides container (goes fullscreen) -->
        <div #slidesContainer class="mt-2" [style]="isFullscreen ? 'background: #000; display: flex; align-items: center; justify-content: center;' : 'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 8px; border: 1px solid #dee2e6; min-height: 480px;'">
          <iframe
            [src]="trainingSlidesUrl"
            [style]="isFullscreen ? 'width: 100%; height: 100%; border: 0;' : 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;'"
            allowfullscreen="true"
            mozallowfullscreen="true"
            webkitallowfullscreen="true">
          </iframe>
        </div>
      }
    </div>

    <div class="ws-save-row mt-4">
      <button mat-stroked-button color="primary" (click)="goToDashboard()" style="padding: 12px 40px; font-size: 16px;">
        Continue to Dashboard
        <i class="bi bi-arrow-right ms-2"></i>
      </button>
    </div>
  </div>
</div>
